import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';

import { LE_AGENT_HUB, LE_AGENT_HUB_CVFR, brands } from '~/consts/brands';
import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';
import { LIST_VISIBILITY } from '~/consts/schedule';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { getAllScheduledRegions } from '~/utils/getAllScheduledRegions';

import isE2ETestOffer from '../../../utils/isE2ETestOffer';

import NewLEDSchedules from './LEDSchedules';
import OldLESchedules from './LESchedulesOld/NewScheduleFrontPage';
import OldLEDSchedules from './NewScheduleByBrand';
import NewScheduleByType from './NewScheduleByType';
import NewSchedules from './common/Schedules';
import { useUndoableCallback } from './common/useUndoableDelete';

interface Props {
  offerId: string;
  offerName: string | undefined;
  schedules: Array<App.Schedule>;
  shouldMigrateSchedule: boolean | undefined;
  migrateScheduleData: () => Promise<void>;
  isMigratingData: boolean;
  migrationError: string | null;
  refreshToken: number;
}

const dataMigratingText = 'Updating...';
const dataMigrationText = 'Clear Schedules';

const getMigrationButtonText = (isMigratingData: boolean, migrationError: string | null) => {
  if (migrationError) {
    return migrationError;
  }
  if (isMigratingData) {
    return dataMigratingText;
  }
  return dataMigrationText;
};

export default function NewSchedulePageEdit({
  offerId,
  offerName,
  schedules,
  shouldMigrateSchedule,
  migrateScheduleData,
  isMigratingData,
  migrationError,
  refreshToken,
}: Props) {
  const [isConfirmClearOpen, setIsConfirmClearOpen] = useState(false);

  const { data: offerData } = useGetOffer(offerId);
  // Prefer the live React Query cache so that schedule additions/deletions are
  // reflected immediately; fall back to the prop for the initial render before
  // the query resolves.
  const currentSchedules = offerData?.brand_schedules ?? schedules;
  const hasSchedules = currentSchedules.length > 0;

  const { confirm: executeClear, isDeleting: isGloballyDeleting } = useUndoableCallback({
    label: 'All schedules cleared',
    onConfirm: migrateScheduleData,
  });

  const handleConfirmClear = useCallback(() => {
    setIsConfirmClearOpen(false);
    executeClear();
  }, [executeClear]);

  const brandsValue = brands.map((brand) => brand.value);
  const otherBrands = [...brands].slice(1);
  const regions = libRegions.getRegions();
  const enabledRegions = regions.filter((r) => !getAllScheduledRegions(currentSchedules).includes(r.code));
  const showE2EOfferWarning = isE2ETestOffer(offerName);

  return (
    <Box>
      <Helmet>
        <title>Offers | {offerName} | Edit Schedule</title>
      </Helmet>
      <Box px={5} pt={3}>
        <OfferBreadcrumbs offerId={offerId} currentPage="Schedules" />
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={5} pt={2} pb={1}>
        <Typography variant="h5">Schedules</Typography>
        <Button
          variant="contained"
          color="error"
          onClick={() => setIsConfirmClearOpen(true)}
          disabled={isMigratingData || !hasSchedules}
        >
          {getMigrationButtonText(isMigratingData, migrationError)}
        </Button>
      </Stack>
      <Dialog open={isConfirmClearOpen} onClose={() => setIsConfirmClearOpen(false)}>
        <DialogTitle>Clear all schedules?</DialogTitle>
        <DialogContent>
          <Typography>This will delete all schedules for this offer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmClearOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmClear} autoFocus>
            Clear Schedules
          </Button>
        </DialogActions>
      </Dialog>
      {showE2EOfferWarning && (
        <Box p={5}>
          <Alert icon={false} variant="filled" severity="error" sx={{ p: 4 }}>
            <Typography variant="h3">This is an E2E test offer</Typography>
            <Typography variant="body1">
              Changing this offer's schedules so that it doesn't appear on the homepage may break the E2E tests. Only
              change these if you know what you're doing!
            </Typography>
          </Alert>
        </Box>
      )}
      {shouldMigrateSchedule && (
        <Box px={5}>
          <Alert severity="warning" sx={{ width: '100%' }}>
            This offer has invalid schedules that are not in the new format or missing some brands. Please clear
            schedules and re-add them to fix this.
          </Alert>
        </Box>
      )}
      <Box px={5}>
        <NewScheduleByType
          schedules={currentSchedules}
          brandsValue={brandsValue}
          offerId={offerId}
          regions={regions}
          shouldMigrateSchedule={shouldMigrateSchedule}
        />
      </Box>
      {window.configs.DISTRIBUTION_CONTROLS_ENABLED === 'on' ? (
        <Stack px={5} pb={5} gap={4}>
          <NewSchedules
            brand="luxuryescapes"
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isExternallyDeleting={isGloballyDeleting}
            refreshToken={refreshToken}
          />
          <NewSchedules
            brand={LE_BUSINESS_TRAVELLER}
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isExternallyDeleting={isGloballyDeleting}
            refreshToken={refreshToken}
          />
          <NewSchedules
            brand={LE_AGENT_HUB}
            affectedBrands={[LE_AGENT_HUB, LE_AGENT_HUB_CVFR]}
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isExternallyDeleting={isGloballyDeleting}
            refreshToken={refreshToken}
            scheduleTypes={[LIST_VISIBILITY]}
          />
          <NewLEDSchedules
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
            isExternallyDeleting={isGloballyDeleting}
            refreshToken={refreshToken}
          />
        </Stack>
      ) : (
        <>
          <OldLESchedules
            offerId={offerId}
            enabledRegions={enabledRegions}
            shouldMigrateSchedule={shouldMigrateSchedule}
          />
          <OldLEDSchedules
            schedules={currentSchedules}
            brands={otherBrands}
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
          />
        </>
      )}
    </Box>
  );
}
